import {
  AddressCategory,
  addressCategoryToField,
  AddressFields,
  AddressSuggestion,
  autocompleteListBoxItemTemplate,
  DependencyError,
  html,
  LookupItem,
  markText,
  render
} from '@cumu/shared';
import AutocompleteElement from '@github/auto-complete-element';
import Autocomplete from '@github/auto-complete-element/dist/autocomplete';
import { controller, target } from '@github/catalyst';
import { HTMLValueElement } from '../forms/utilities';
import { addCustomValidator } from '../forms/validation';
import {
  getReportParameters,
  validateReportParameterReferences
} from '../report-parameters';
import { isSessionExpiredResponse } from '../session';
import { dispatchChange } from './util';

// fire event when results html is added to the menu
// https://github.com/github/auto-complete-element/blob/8df0008b15af3dc5064db09e15533b77e3f70033/src/autocomplete.ts#L209
const standardIdentifyOptions = Autocomplete.prototype.identifyOptions;
Autocomplete.prototype.identifyOptions = function (...args) {
  this.container.dispatchEvent(
    new CustomEvent<{ results: HTMLElement; query: string }>(
      'auto-complete-results-html-rendered',
      {
        bubbles: true,
        detail: { results: this.results, query: this.input.value.trim() }
      }
    )
  );
  standardIdentifyOptions.call(this, ...args);
};

@controller('autocomplete-list-box')
class AutocompleteListBoxElement extends HTMLElement {
  @target declare list: HTMLUListElement;
  @target declare autocomplete: AutocompleteElement;
  @target declare valueInput: HTMLInputElement;
  @target declare autocompleteInput: HTMLInputElement;
  @target declare autocompleteInputItem: HTMLLIElement;

  get parametric() {
    return this.hasAttribute('parametric');
  }

  connectedCallback() {
    document.body.addEventListener('focus', this, { capture: true });
    document.body.addEventListener('click', this);
  }

  disconnectedCallback() {
    document.body.removeEventListener('focus', this, { capture: true });
    document.body.removeEventListener('click', this);
  }

  getValue() {
    const current: LookupItem[] = Array.from(
      this.list.querySelectorAll('[data-list-box-value]')
    ).map(el => JSON.parse(el.getAttribute('data-list-box-value')!));
    if (current.length === 0) {
      return null;
    }
    return current;
  }

  synchronize() {
    if (
      this.hasAttribute('required') &&
      !this.list.querySelectorAll('[data-list-box-value]').length
    ) {
      this.autocompleteInput.required = true;
    } else {
      this.autocompleteInput.required = false;
    }
    this.valueInput.value = JSON.stringify(this.getValue());
    dispatchChange(this.valueInput);
  }

  clear() {
    this.list
      .querySelectorAll('[data-list-box-value]')
      .forEach(el => el.remove());
    this.synchronize();
  }

  handleInputKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter' && this.autocompleteInput.value.length) {
      event.preventDefault();
      return;
    }

    if (
      event.key === 'Backspace' &&
      this.autocompleteInput.value === '' &&
      this.autocompleteInputItem.previousElementSibling instanceof
        HTMLElement &&
      !event.metaKey &&
      !event.altKey &&
      !event.ctrlKey &&
      !event.shiftKey
    ) {
      this.autocompleteInputItem.previousElementSibling.click();
      this.list.dispatchEvent(new KeyboardEvent(event.type, event));
    }
  }

  async comboboxCommit(event: CustomEvent) {
    this.autocomplete.value = '';

    if (!(event.target instanceof Element)) {
      return;
    }

    let value = JSON.parse(
      event.target.getAttribute('data-autocomplete-data')!
    ) as LookupItem | AddressSuggestion;

    let item: LookupItem;

    if ('magicKey' in value) {
      const params = new URLSearchParams({
        q: value.text,
        'magic-key': value.magicKey,
        'for-storage': 'true'
      });

      this.autocompleteInputItem.insertAdjacentHTML(
        'beforebegin',
        render(
          html`<li
            class="autocomplete-list-box-item autocomplete-list-box-item-option"
          >
            <span class="spinner-border color-fg-muted"></span>
          </li>`
        )
      );
      const spinner = this.autocompleteInputItem.previousElementSibling!;
      const response = await fetch(`/api/addresses/candidates?${params}`);
      spinner.remove();
      if (isSessionExpiredResponse(response)) {
        return;
      }
      if (!response.ok) {
        throw new DependencyError(response);
      }
      const [candidate] = (await response.json()) as AddressFields[];
      const category = new URL(
        this.autocomplete.src,
        location.href
      ).searchParams.get('category') as AddressCategory;
      const field = addressCategoryToField[category];
      item = {
        id: candidate[field],
        title: candidate[field]
      };
    } else {
      item = value;
    }

    const current = this.getValue() || [];

    if (current.find(x => x.id === item.id)) {
      return;
    }

    this.autocompleteInputItem.insertAdjacentHTML(
      'beforebegin',
      render(autocompleteListBoxItemTemplate(item, false))
    );

    this.synchronize();
    this.focusAutocompleteInput();
  }

  listBoxItemDelete() {
    if (this.list.querySelectorAll('[data-list-box-value]').length === 0) {
      this.autocompleteInput.focus();
    }
    Promise.resolve().then(() => this.synchronize());
  }

  focusAutocompleteInput() {
    this.autocompleteInput.focus();
  }

  handleEvent(event: Event) {
    if (
      ['focus', 'click'].includes(event.type) &&
      !this.contains(event.target as Element)
    ) {
      this.autocompleteInput.value = '';
      this.autocomplete.open = false;
    }
  }

  resultsAdded(event: CustomEvent<{ results: HTMLElement; query: string }>) {
    if (!this.parametric || !this.valueInput?.form) {
      return;
    }
    const { results, query } = event.detail;
    const parameters = getReportParameters(this.valueInput).filter(x =>
      x.name.toLowerCase().includes(query.toLowerCase())
    );
    if (!parameters?.length) {
      return;
    }
    results.insertAdjacentHTML(
      'afterbegin',
      render(
        parameters.map(
          ({ name }) =>
            html`<li
              role="option"
              class="autocomplete-item"
              data-autocomplete-value="${name}"
              data-autocomplete-data="${JSON.stringify({
                id: name,
                title: name
              })}"
            >
              ${markText(name, query)}
            </li>`
        )
      )
    );
  }
}

declare global {
  interface Window {
    AutocompleteListBoxElement: typeof AutocompleteListBoxElement;
  }
  interface HTMLElementTagNameMap {
    'autocomplete-list-box': AutocompleteListBoxElement;
  }
}

addCustomValidator((input: HTMLValueElement, _label: string) => {
  if (
    input instanceof HTMLInputElement &&
    input
      .getAttribute('data-target')
      ?.includes('autocomplete-list-box.autocompleteInput')
  ) {
    const box = input.closest('autocomplete-list-box');
    if (!box || !box.parametric || box.autocompleteInput !== input) {
      return null;
    }
    const items = JSON.parse(box.valueInput.value) as LookupItem[];
    if (items.length === 0) {
      return null;
    }
    const result = validateReportParameterReferences(
      box.valueInput,
      items.map(x => x.id as string)
    );
    return result.valid ? null : result.message;
  }
  return null;
});
